import CtaIcon from '../ui/CtaIcon';
import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  collectorRequest,
  collectorPhoto,
  type CollectionEntry,
} from '../../services/api/collector';
import { listSpotDrafts, removeSpotDraft, type SpotDraft } from '../../services/collectorDrafts';
import { cardRequest } from '../../services/api/passportCards';
import { imageSaveMessage, saveImage } from '../../utils/saveImage';
import { isRunningInBiltNativeShell } from '../../utils/nativeShell';
import './passport-cabinet.css';
function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
export default function PassportArchive() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <div className="pc-page">Opening your archive…</div>;
  if (!user) return <Navigate to="/login" state={{ from: '/passport/archive' }} replace />;
  return <Archive key={user.id} userId={Number(user.id)} />;
}
function Archive({ userId }: { userId: number }) {
  const [entries, setEntries] = useState<CollectionEntry[]>([]);
  const [drafts, setDrafts] = useState<SpotDraft[]>([]);
  const [error, setError] = useState('');
  const [photoFeedback, setPhotoFeedback] = useState<{ id: string; text: string; error: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  useEffect(() => {
    let active = true;
    void Promise.all([
      collectorRequest<{ items: CollectionEntry[]; pages: number }>(
        'list',
        {},
        { limit: '100', source: 'photo' }
      ),
      listSpotDrafts(userId),
    ])
      .then(([e, d]) => {
        if (active) {
          setEntries(e.items.filter((i) => i.spots.length > 0));
          setPages(e.pages);
          setDrafts(d);
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [userId]);
  async function exportAll() {
    try {
      const [archive, cards] = await Promise.all([
        collectorRequest('export'),
        cardRequest('export'),
      ]);
      download(
        new Blob([JSON.stringify({ archive, cards }, null, 2)], { type: 'application/json' }),
        'bilt-passport-export.json'
      );
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function savePhoto(photo: Blob, filename: string, id: string) {
    setPhotoFeedback({ id, text: 'Saving photo…', error: false });
    try {
      setPhotoFeedback({ id, text: imageSaveMessage(await saveImage(photo, filename)), error: false });
    } catch (e) {
      setPhotoFeedback({ id, text: (e as Error).message, error: true });
    }
  }
  function feedback(id: string) {
    if (photoFeedback?.id !== id) return null;
    return <p role={photoFeedback.error ? 'alert' : 'status'} className={photoFeedback.error ? 'pc-error' : 'pc-muted'}>{photoFeedback.text}</p>;
  }
  return (
    <div className="pc-page">
      <Link className="pc-link" to="/passport">
        <CtaIcon direction="back" /> Your Passport
      </Link>
      <div className="pc-intro">
        <span className="pc-eyebrow">Private photo archive</span>
        <h1>
          Your memories
          <br />
          are still here.
        </h1>
        <p>
          Passport now collects through QR. Your saved photos and notes stay private. Save them,
          revisit them, or remove them whenever you choose.
        </p>
      </div>
      {error && (
        <p role="alert" className="pc-error">
          {error}
        </p>
      )}
      <button className="pc-button secondary" onClick={exportAll}>
        Export Passport records
      </button>
      <p className="pc-muted">
        Includes your card records and photo notes. Save archived photos individually below.
      </p>
      <section className="pc-section">
        <h2>Drafts on this device</h2>
        <p className="pc-muted">
          Drafts have stopped uploading automatically. They stay here until you save or delete
          them. {isRunningInBiltNativeShell() ? 'Images save to your Photos library.' : ''}
        </p>
        {drafts.map((d) => (
          <div className="pc-activity" key={d.id}>
            <div>
              <strong>{d.title || 'Untitled photo'}</strong>
              <p className="pc-muted">{d.note}</p>
            </div>
            <div className="pc-actions">
              <button
                className="pc-link"
                onClick={() => void savePhoto(d.photo, `bilt-draft-${d.id}.jpg`, d.id)}
              >
                {isRunningInBiltNativeShell() ? 'Save photo to Photos' : 'Download photo'}
              </button>
              <button
                className="pc-link"
                onClick={() =>
                  download(
                    new Blob([JSON.stringify({ ...d, photo: undefined }, null, 2)], {
                      type: 'application/json',
                    }),
                    `bilt-draft-${d.id}.json`
                  )
                }
              >
                Save notes
              </button>
              <button
                className="pc-link"
                onClick={async () => {
                  if (
                    window.confirm(
                      'Delete this draft from this device? Download it first if you want to keep it.'
                    )
                  ) {
                    await removeSpotDraft(d.id, userId);
                    setDrafts((ds) => ds.filter((x) => x.id !== d.id));
                  }
                }}
              >
                Delete draft
              </button>
            </div>
            {feedback(d.id)}
          </div>
        ))}
        {!drafts.length && !loading && <p className="pc-muted">No drafts saved on this device.</p>}
      </section>
      <section className="pc-section">
        <h2>Saved photos</h2>
        {loading && <p role="status">Opening your saved memories…</p>}
        {entries.map((e) => (
          <div className="pc-activity" key={e.key}>
            <Link className="pc-link" to={`/passport/car?entry=${encodeURIComponent(e.key)}`}>
              {e.title}
            </Link>
            <div className="pc-actions">
              {e.spots.map((s, i) => (
                <button
                  key={s.id}
                  className="pc-link"
                  onClick={async () => {
                    setPhotoFeedback({ id: s.id, text: 'Opening photo…', error: false });
                    try {
                      await savePhoto(await collectorPhoto(s.id), `bilt-photo-${s.id}.jpg`, s.id);
                    } catch (err) {
                      setPhotoFeedback({ id: s.id, text: (err as Error).message, error: true });
                    }
                  }}
                >
                  {isRunningInBiltNativeShell() ? 'Save photo to Photos' : 'Download photo'} {e.spots.length > 1 ? i + 1 : ''}
                </button>
              ))}
            </div>
            {feedback(e.spots.find((s) => s.id === photoFeedback?.id)?.id || '')}
          </div>
        ))}
        {page < pages && (
          <button
            className="pc-link"
            onClick={async () => {
              try {
                const next = await collectorRequest<{ items: CollectionEntry[]; pages: number }>(
                  'list',
                  {},
                  { source: 'photo', page: String(page + 1) }
                );
                setEntries((old) => [...old, ...next.items]);
                setPage(page + 1);
                setPages(next.pages);
              } catch (e) {
                setError((e as Error).message);
              }
            }}
          >
            Load older photos
          </button>
        )}
        {!entries.length && !loading && (
          <p className="pc-muted">
            No saved photo memories in this view. Your exported records include the complete
            archive.
          </p>
        )}
      </section>
    </div>
  );
}
